import { useSyncExternalStore } from 'react';
import type { RenderAdapter } from '~/viewportGrid';

/**
 * The renderer of the grid tile the user activated, held for controls that
 * act on that tile from outside the grid. It is published on every
 * activation change and withdrawn when no tile is active or the grid is
 * gone, so a control never acts on a tile that is gone. The renderer is a
 * live handle into the grid, which is why this is a store and not an atom:
 * it is never snapshotted or serialised. Beside it the store holds whether
 * a pane is activated at all, so a control can tell a pane that shows no
 * image (a panel) from no activation.
 */
class ActiveTileRendererStore {
    private renderer: RenderAdapter | undefined;
    private paneActivated = false;
    private readonly listeners = new Set<() => void>();

    public readonly read = (): RenderAdapter | undefined => this.renderer;

    public readonly readPaneActivated = (): boolean => this.paneActivated;

    public readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    public publish(
        renderer: RenderAdapter | undefined,
        paneActivated: boolean
    ): void {
        if (
            renderer === this.renderer &&
            paneActivated === this.paneActivated
        ) {
            return;
        }
        this.renderer = renderer;
        this.paneActivated = paneActivated;
        this.listeners.forEach((listener) => listener());
    }
}

const store = new ActiveTileRendererStore();

/**
 * Publishes the activated tile's renderer, or undefined when none is.
 * `paneActivated` says whether a grid pane is activated at all; it is true
 * with no renderer while the activated pane shows no image.
 */
export function publishActiveTileRenderer(
    renderer: RenderAdapter | undefined,
    paneActivated: boolean = renderer !== undefined
): void {
    store.publish(renderer, paneActivated);
}

/** The activated tile's renderer, or undefined when no tile is activated. */
export function useActiveTileRenderer(): RenderAdapter | undefined {
    return useSyncExternalStore(store.subscribe, store.read);
}

/** Whether a grid pane is activated: a tile, or a pane that shows no image. */
export function useIsGridPaneActivated(): boolean {
    return useSyncExternalStore(store.subscribe, store.readPaneActivated);
}
